import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface DistrictSchool {
  school_id: string;
  school_name: string;
  district: string;
  is_current_school?: boolean;
  grade_range?: string;
  uses_sessions?: boolean;
  session?: string;
}

/**
 * Centralized hook for fetching and caching user's district schools.
 *
 * This hook prevents duplicate RPC calls to get_user_district_schools by:
 * 1. Using React Query's built-in caching (5 minute stale time)
 * 2. Deduplicating concurrent requests
 * 3. Sharing cache across all components
 *
 * Usage:
 * ```tsx
 * const { data: districtSchools, isLoading } = useUserDistrictSchools();
 * const schoolIds = districtSchools?.map(s => s.school_id) || [];
 * ```
 */
export const useUserDistrictSchools = (enabled = true) => {
  return useQuery({
    queryKey: ['userDistrictSchools'],
    queryFn: async (): Promise<DistrictSchool[]> => {
      const { data, error } = await supabase.rpc('get_user_district_schools');

      if (error) {
        console.error('Error fetching district schools:', error);
        throw error;
      }

      return (data || []) as DistrictSchool[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - district schools rarely change
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 2,
    enabled,
    refetchOnWindowFocus: false, // Don't refetch on tab focus
    refetchOnMount: false, // Use cached data if available
  });
};

/**
 * Helper hook to get just the school IDs from district schools.
 * Useful for queries that need an array of school IDs.
 */
export const useDistrictSchoolIds = (enabled = true) => {
  const { data: districtSchools, ...rest } = useUserDistrictSchools(enabled);

  const schoolIds = districtSchools?.map(s => s.school_id).filter(Boolean) || [];

  return {
    schoolIds,
    districtSchools,
    ...rest,
  };
};

/**
 * Prefetch district schools data.
 * Call this early (e.g., on dashboard mount) to warm the cache.
 */
export const prefetchDistrictSchools = async (queryClient: any) => {
  await queryClient.prefetchQuery({
    queryKey: ['userDistrictSchools'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_user_district_schools');
      if (error) throw error;
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

export default useUserDistrictSchools;
